/**
 * Third-party vendor API endpoints and URLs.
 * These are public endpoints that don't change between environments.
 */

export const GARMIN_URLS = {
  // OAuth endpoints
  OAUTH_CONFIRM: 'https://connect.garmin.com/oauthConfirm',
  OAUTH2_CONFIRM: 'https://connect.garmin.com/oauth2Confirm',
  OAUTH_REQUEST_TOKEN:
    'https://connectapi.garmin.com/oauth-service/oauth/request_token',
  OAUTH_ACCESS_TOKEN:
    'https://connectapi.garmin.com/oauth-service/oauth/access_token',
  OAUTH1_TO_OAUTH2_TOKEN_EXCHANGE:
    'https://apis.garmin.com/partner-gateway/rest/user/token-exchange',
  OAUTH2_TOKEN: 'https://diauth.garmin.com/di-oauth2-service/oauth/token',
  USER_REGISTRATION:
    'https://apis.garmin.com/wellness-api/rest/user/registration',

  // API Base URLs
  API_BASE: 'https://apis.garmin.com',

  // Specific API endpoints
  WELLNESS_API: 'https://apis.garmin.com/wellness-api/rest',
  TRAINING_API: 'https://apis.garmin.com/training-api',
  USER_PERMISSIONS: 'https://apis.garmin.com/userPermissions',
} as const;

export const STRAVA_URLS = {
  // OAuth endpoints
  OAUTH_AUTHORIZE: 'https://www.strava.com/oauth/authorize',
  OAUTH_TOKEN: 'https://www.strava.com/api/v3/oauth/token',

  // API Base URLs
  API_V3_BASE: 'https://www.strava.com/api/v3',

  // Specific API endpoints
  ATHLETE: 'https://www.strava.com/api/v3/athlete',
  ACTIVITIES: 'https://www.strava.com/api/v3/athlete/activities',
  PUSH_SUBSCRIPTIONS: 'https://www.strava.com/api/v3/push_subscriptions',

  // Web pages
  DASHBOARD: 'https://www.strava.com/dashboard',
} as const;

// Helper functions for building URLs
export const garminApi = {
  workout: (workoutId?: string) =>
    workoutId
      ? `${GARMIN_URLS.TRAINING_API}/workout/${workoutId}`
      : `${GARMIN_URLS.TRAINING_API}/workout`,

  schedule: (scheduleId?: string) =>
    scheduleId
      ? `${GARMIN_URLS.TRAINING_API}/schedule/${scheduleId}`
      : `${GARMIN_URLS.TRAINING_API}/schedule/`,

  oauthConfirm: (oauthToken: string, callbackUrl: string) =>
    `${GARMIN_URLS.OAUTH_CONFIRM}?oauth_token=${oauthToken}&oauth_callback=${encodeURIComponent(callbackUrl)}`,

  oauth2Confirm: ({
    clientId,
    codeChallenge,
    redirectUri,
    state,
  }: {
    clientId: string;
    codeChallenge: string;
    redirectUri: string;
    state: string;
  }) => {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      redirect_uri: redirectUri,
      state,
    });
    return `${GARMIN_URLS.OAUTH2_CONFIRM}?${params.toString()}`;
  },
} as const;

export const stravaApi = {
  activity: (activityId: string) =>
    `${STRAVA_URLS.API_V3_BASE}/activities/${activityId}`,

  authorizeUrl: (
    clientId: string,
    redirectUri: string,
    scope: string,
    state?: string
  ) => {
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope,
      ...(state && { state }),
      _prompt: 'force',
    });
    return `${STRAVA_URLS.OAUTH_AUTHORIZE}?${params.toString()}`;
  },
} as const;
